import type { UserAuthComponents, UserAuthPaths, UseQueryOptions } from '../../types'
import type { Brand as UserAuthBrand } from '@core/types/clients/user-auth'
import type { Merge } from 'type-fest'
import { useOpenapi, useApi, useQuery, useCoreConfig, useUserInfo, useUsernameCookie, useMutation, navigateTo, OpenapiError, passwordResetErrorClass, userAuthErrorClass } from '#imports'

/**
 * @title useUserAuthClient
 * @see Docs https://user-auth-prod.spilnu.dk/api/documentation
 */
export const useUserAuthClient = () => {
    const { site } = useCoreConfig()
    const client = useOpenapi<UserAuthPaths>({
        baseUrl: '/gateway/user-auth/api',
        service: 'userAuth'
    })

    const { isLoggedIn } = useUserInfo()

    const getLoginLimitsCheck = (options: Pick<UseQueryOptions<any>, 'enabled' | 'lazy' | 'server'> = {}) => {
        const { enabled = isLoggedIn, lazy = true, server = false } = options

        const query = useQuery(async ({ signal }) => {
            const response = await client.GET('/v1/public/login-limits-check', { signal })

            return response
        }, {
            key: 'userAuth:loginLimitsCheck',
            server,
            lazy,
            enabled
        })

        return query
    }

    const getSessionInfo = (options: Pick<UseQueryOptions<any>, 'enabled' | 'lazy' | 'server'> = {}) => {
        const { enabled = isLoggedIn, lazy = true, server = false } = options

        const query = useQuery(async ({ signal }) => {
            const { data } = await client.GET('/v1/public/session-info', {
                signal,
                params: {
                    query: {
                        brand: site.brand
                    }
                }
            })

            return data
        }, {
            key: 'userAuth:sessionInfo',
            server,
            lazy,
            enabled
        })

        return query
    }

    const api = useApi()

    const startMobilePay = async ({ signup, promotionId, promotionAccepted }: { signup: boolean, promotionId?: string, promotionAccepted?: boolean }) => {
        const { data } = await client.POST('/v1/public/oidc/mobilepay/start', {
            body: {
                brand: site.brand as UserAuthBrand,
                signup: signup ? 'true' : 'false',
                promotionId,
                promotionAccepted: promotionAccepted === undefined ? undefined : String(promotionAccepted)
            }
        })

        return data
    }

    const mobilePayError = async ({ error, error_description }: { error: string, error_description: string }) => {
        await client.POST('/v1/public/oidc/mobilepay/error', {
            body: {
                brand: site.brand as UserAuthBrand,
                error,
                error_description
            }
        })
    }

    interface ExchangeMobilePayParams {
        code: string
        state: string
    }

    const exchangeMobilePay = async (params: ExchangeMobilePayParams) => {
        const data = await api<Merge<UserAuthComponents['schemas']['LoginResponse'], { details?: { reason?: string | null } }>>('/gateway/user-auth/api/v1/public/oidc/mobilepay/exchange', {
            method: 'POST',
            body: {
                code: params.code,
                state: params.state,
                brand: site.brand
            },
            // useApi sets no timeout, and this runs on /eid/callback where a stalled connection
            // — common right after hopping back from a native app — would await forever behind
            // an unconditional loader.
            timeout: 15_000,
            ignoreResponseError: true
        })

        return data
    }

    function useLogout(redirectUrl?: string) {
        const { client } = useUserAuthClient()

        const { userJustLoggedIn } = useUserInfo()
        const usernameCookie = useUsernameCookie()

        function redirectOnLogout() {
            // Tidy only: the cookie is keyed to the player, so the next one would not read it anyway.
            usernameCookie.value = undefined
            // User is already logged out, reload window
            navigateTo(redirectUrl || '/', { external: true })
        }

        const mutation = useMutation(async () => {
            const response = await client.POST('/v1/public/auth/logout')

            return response
        }, {
            onSuccess() {
            // Unset userJustLoggedIn cookie
                userJustLoggedIn.value = undefined

                redirectOnLogout()
            },
            onError(err) {
                if (OpenapiError.isError(err)) {
                    if (err.status === 400) {
                        redirectOnLogout()
                    }
                }
            },
        })

        return mutation
    }

    function useRequestPasswordReset() {
        return useMutation(async (email: string) => {
            const { data } = await client.POST('/v1/public/password-resets', {
                body: {
                    email,
                    brand: site.brand as UserAuthBrand
                }
            })

            return data
        }, {
            mode: 'leading',
            errorClass: passwordResetErrorClass,
        })
    }

    function useResetPassword() {
        return useMutation(async (body: UserAuthComponents['schemas']['ResetPasswordRequest']) => {
            const { data } = await client.PUT('/v1/public/password-resets', { body })

            return data
        }, {
            errorClass: passwordResetErrorClass,
        })
    }

    function useChangePassword() {
        return useMutation(async (body: UserAuthComponents['schemas']['ChangeOwnPasswordRequest']) => {
            const { data } = await client.PUT('/v1/public/password', { body })

            return data
        }, {
            errorClass: userAuthErrorClass([], error => error.status === 422 || error.status === 429),
        })
    }

    return {
        client,
        getLoginLimitsCheck,
        getSessionInfo,
        useLogout,
        useRequestPasswordReset,
        useResetPassword,
        useChangePassword,
        startMobilePay,
        mobilePayError,
        exchangeMobilePay
    }
}
